import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n, useLicenses, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import loyaltyIcon from '~Assets/icons/loyalty.svg';
import arrowForwardIcon from '~Assets/icons/arrow_forward.svg';
import { useExpressCart } from '../../../../Providers/ExpressCartProvider';
import LoyaltyModal from './LoyaltyModal';
import { useStorage } from '~Components/Storage';
import useSessionActive from '../../../../Hooks/useSessionActive';

/** Same box as the promo code row, pinned under the list in both shells. */
const Row = styled.div`
  flex: 0 0 auto;
  margin: 0 20px 12px;
  padding: 16px 22px;
  border-radius: 18px;
  border: 2px solid var(--eposButton);
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  user-select: none;
  color: var(--eposText);
  transition: transform 0.15s ease-out;

  &.is-touching {
    transform: scale(0.98);
  }
`;

const Text = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.span`
  font-size: 19px;
  font-weight: 800;
  line-height: 1.2;
`;

const Body = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--eposMutedText);
`;

/**
 * "Member? Identify yourself here" - opens LoyaltyModal, which asks the
 * terminal for a card. Only with Loyalty Pro, and gone once a member is on
 * the cart - LoyaltyBanner takes over then.
 */
const LoyaltyHint: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-loyalty-hint');
  const licenses = useLicenses();
  const expressCart = useExpressCart();
  const [storage] = useStorage();

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => {
    logger.info('Loyalty modal opened from the cart');
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => setOpen(false), []);

  /** The idle timeout (or start over) must not leave the modal up for the next shopper. */
  const sessionActive = useSessionActive();
  useEffect(() => {
    if (!sessionActive) {
      logger.info('Session ended - closing the loyalty modal');
      setOpen(false);
    }
  }, [sessionActive]);

  const { touching, disabled, disabledToast, ...rowButton } = useButton({ onClick: handleOpen });

  if (
    !licenses.hasLoyaltyPro ||
    expressCart.loyalty?.memberId ||
    storage.adyenTerminals.length === 0
  ) {
    return null;
  }

  return (
    <>
      <Row
        {...rowButton}
        role="button"
        className={touching ? 'is-touching' : undefined}
        data-testid="epos-loyalty-hint">
        <Icon src={loyaltyIcon} color="var(--eposButton)" width="28px" height="28px" />
        <Text>
          <Title>{i18n('Express.Loyalty.Hint.Title')}</Title>
          <Body>{i18n('Express.Loyalty.Hint.Body')}</Body>
        </Text>
        <Icon src={arrowForwardIcon} color="var(--eposText)" width="18px" height="18px" />
      </Row>
      <LoyaltyModal open={open} onClose={handleClose} />
    </>
  );
};

export default memo(LoyaltyHint);
